import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Settings as SettingsIcon, Loader2, Save, AlertCircle, CheckCircle2,
  Sliders, DollarSign, Power, Sparkles, MapPin, Brain, Mail,
  Mic, Globe, BellOff, MessageSquare,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDateTime } from "@/lib/dates";

export default function AdminConfig() {
  const { t } = useTranslation();

  const NUMBER_FIELDS = [
    { key: "ai_ceiling_usd", icon: DollarSign, step: 10 },
    // The ceiling's four steps, in percent of it (DY1.7): alert, every text
    // channel on the backup model, voice off, everything off until the 1st.
    // The server refuses a set that does not rise in that order.
    { key: "ai_ceiling_alert_pct", icon: Sliders, step: 5 },
    { key: "ai_ceiling_backup_pct", icon: Sliders, step: 5 },
    { key: "ai_ceiling_voice_off_pct", icon: Sliders, step: 1 },
    { key: "ai_ceiling_off_pct", icon: Sliders, step: 1 },
    { key: "ai_quota_month_client", icon: Sliders, step: 1 },
    // A FixyBee's Dany allowance is their plan's quotes a month times this
    // (PLANES): 20 gives Starter 60, Pro 240, Master 500. There is no per-tier
    // AI field any more — the quotes fields below are what move it.
    { key: "ai_quota_multiplier", icon: Sliders, step: 1 },
    { key: "extra_credit_price_usd", icon: DollarSign, step: 0.5 },
    { key: "extra_credit_max_per_purchase", icon: Sliders, step: 5 },
    // Quotes a month per plan — the one thing the three plans differ in
    // (PLANES, 2026-09-30). Read live by the credit check, the plan cards and
    // Dany, so repricing a plan's allowance is these three fields.
    { key: "quotes_month_starter", icon: Sliders, step: 1 },
    { key: "quotes_month_pro", icon: Sliders, step: 1 },
    { key: "quotes_month_master", icon: Sliders, step: 1 },
    { key: "max_upload_size_mb", icon: Sliders, step: 1 },
    // How far "near you" reaches. One number for the provider search, Dany's
    // find_fixybees and FixyMatch's push, so widening coverage in a new city is
    // this field and nothing else.
    { key: "search_radius_miles", icon: MapPin, step: 5 },
    { key: "dany_memory_inactivity_minutes", icon: Sliders, step: 5 },
    // Voice minutes are the allowance a caller actually feels, and the rate is
    // what turns them into money. Both move without a deploy (DY1.6).
    { key: "voice_minutes_month_user", icon: Mic, step: 5 },
    { key: "voice_cost_per_minute_usd", icon: DollarSign, step: 0.01 },
    // How long one chat thread stays quiet after alerting the same person
    // (#17). OneSignal re-rings on every collapse, so this is the only layer
    // that can not-ring; 45s is a guess about how people text and this field
    // is where the first week of real use corrects it. 0 disables it.
    { key: "chat_push_debounce_seconds", icon: BellOff, step: 5 },
  ];

  // Free text, one field each. The alert list is comma-separated addresses;
  // the server normalises it and refuses an empty one.
  const TEXT_FIELDS = [
    { key: "ai_alert_emails", icon: Mail },
  ];

  // A key with no approved label yet is named by the key itself, which is
  // exact if not pretty — never by a label nobody approved (DY1.7).
  const label = (key) => t(`adminX.config.fields.${key}`, { defaultValue: key });

  const BOOL_FIELDS = [
    { key: "ai_kill_switch_active", icon: Power, danger: true },
    { key: "feature_ai_smart_description", icon: Sparkles },
    { key: "feature_ai_image_diagnosis", icon: Sparkles },
    { key: "feature_ai_comparator", icon: Sparkles },
    { key: "feature_ai_estimator", icon: Sparkles },
    { key: "feature_ai_search_intent", icon: Sparkles },
    { key: "feature_ai_suggest_categories", icon: Sparkles },
    // Dany by text, on its own (DY1.7 M5): off answers the chat with the
    // "temporarily unavailable" line; voice and the job form are untouched.
    { key: "feature_dany_text_enabled", icon: MessageSquare },
    // The one AI cost centre that spends on a schedule (DY1.5).
    { key: "feature_dany_memory", icon: Brain },
    // Voice off has to be possible without taking text down with it — voice is
    // the expensive channel (DANY.md 2.6, layer 3).
    { key: "feature_dany_voice", icon: Mic },
    // Jorge's exit criterion of 2026-09-10, as a switch rather than a plan: off
    // means a chat message no longer pushes to anyone who has not said they
    // want it, and the bell carries the conversation on its own.
    { key: "feature_push_chat_messages", icon: MessageSquare },
  ];

  // Market price search (DANY.md 2.8). Its own section rather than five more
  // rows in the two lists above: the kill switch and the numbers that bound it
  // are one control surface, and the switch is the reason the rest exist.
  // Seeded since DY2.2 and respected by services/dany/market_prices.py — until
  // 2026-09-08 they were simply not writable, so turning the feature off was an
  // UPDATE against the database by hand.
  const MARKET_SEARCH_NUMBERS = [
    { key: "dany_market_search_per_draft", icon: Sliders, step: 1 },
    { key: "dany_market_search_per_day_user", icon: Sliders, step: 1 },
    { key: "dany_market_search_per_day_platform", icon: Sliders, step: 10 },
    { key: "market_search_cost_per_call_usd", icon: DollarSign, step: 0.001 },
  ];

  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/config");
      setCfg(data || {});
      setDraft({});
    } catch (e) {
      setMsg({ type: "err", text: errorMessage(e, t("adminX.config.couldNotLoad")) });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const dirty = Object.keys(draft).length > 0;
  const value = (key) => (key in draft ? draft[key] : cfg?.[key]);

  const setField = (key, v) => setDraft({ ...draft, [key]: v });

  const save = async () => {
    if (!dirty) return;
    setMsg(null);
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/config", draft);
      setCfg(data);
      setDraft({});
      setMsg({ type: "ok", text: t("adminX.config.saved") });
    } catch (e) {
      setMsg({ type: "err", text: errorMessage(e, t("adminX.config.couldNotSave")) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-config-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-worker flex items-center gap-2">
              <SettingsIcon className="text-hive-ink" /> {t("adminX.config.title")}
            </h1>
            <p className="mt-2 text-smoke">{t("adminX.config.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            {dirty && <span className="text-xs text-amber-700 font-display">{t("adminX.config.unsaved")}</span>}
            <button onClick={save} disabled={saving || !dirty} className="fx-btn fx-btn--primary" data-testid="save-config-btn">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              {t("adminX.config.saveBtn")}
            </button>
          </div>
        </div>

        {msg && (
          <div className={`mb-4 p-3 rounded-sm text-sm ${msg.type === "ok" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="config-msg">
            {msg.type === "ok" ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertCircle size={14} className="inline mr-1" />}
            {msg.text}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-smoke">
            <Loader2 className="animate-spin mr-2" /> {t("adminX.jobs.loading")}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-base p-6" data-testid="numeric-section">
              <h2 className="font-display text-lg font-bold text-worker mb-4">{t("adminX.config.numeric")}</h2>
              <div className="space-y-3">
                {NUMBER_FIELDS.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div key={f.key}>
                      <label className="label-base flex items-center gap-1">
                        <Icon size={12} /> {label(f.key)}
                      </label>
                      <input
                        type="number"
                        step={f.step}
                        value={value(f.key) ?? ""}
                        onChange={(e) => setField(f.key, e.target.value === "" ? null : Number(e.target.value))}
                        className="input-base"
                        data-testid={`config-${f.key}`}
                      />
                    </div>
                  );
                })}
                {TEXT_FIELDS.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div key={f.key}>
                      <label className="label-base flex items-center gap-1">
                        <Icon size={12} /> {label(f.key)}
                      </label>
                      <input
                        type="text"
                        value={value(f.key) ?? ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        className="input-base"
                        data-testid={`config-${f.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card-base p-6" data-testid="boolean-section">
              <h2 className="font-display text-lg font-bold text-worker mb-4">{t("adminX.config.flags")}</h2>
              <div className="space-y-2">
                {BOOL_FIELDS.map((f) => {
                  const Icon = f.icon;
                  const v = !!value(f.key);
                  return (
                    <label key={f.key}
                      className={`flex items-center justify-between gap-3 p-3 rounded-sm cursor-pointer transition ${
                        f.danger && v ? "bg-red-50 border border-red-200" : "hover:bg-linen"
                      }`}
                      data-testid={`config-${f.key}`}>
                      <span className="flex items-center gap-2 text-sm text-worker">
                        <Icon size={14} className={f.danger ? "text-red-600" : "text-hive-ink"} />
                        {label(f.key)}
                      </span>
                      <input
                        type="checkbox"
                        checked={v}
                        onChange={(e) => setField(f.key, e.target.checked)}
                        className="w-5 h-5 accent-hive-deep"
                      />
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="card-base p-6 lg:col-span-2" data-testid="market-search-section">
              <h2 className="font-display text-lg font-bold text-worker mb-1 flex items-center gap-2">
                <Globe size={18} className="text-hive-ink" /> {t("adminX.config.marketSearch")}
              </h2>
              <p className="text-xs text-smoke mb-4">{t("adminX.config.marketSearchHint")}</p>

              {/* The switch first, and styled like the AI kill switch: this is
                  the control someone comes to this page to find in a hurry. */}
              <label
                className={`flex items-center justify-between gap-3 p-3 rounded-sm cursor-pointer transition mb-4 ${
                  value("feature_dany_search") === false
                    ? "bg-red-50 border border-red-200"
                    : "hover:bg-linen"
                }`}
                data-testid="config-feature_dany_search">
                <span className="flex items-center gap-2 text-sm text-worker">
                  <Power size={14} className={value("feature_dany_search") === false ? "text-red-600" : "text-hive-ink"} />
                  {t("adminX.config.fields.feature_dany_search")}
                </span>
                <input
                  type="checkbox"
                  checked={value("feature_dany_search") !== false}
                  onChange={(e) => setField("feature_dany_search", e.target.checked)}
                  className="w-5 h-5 accent-hive-deep"
                />
              </label>

              <div className="grid sm:grid-cols-2 gap-3">
                {MARKET_SEARCH_NUMBERS.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div key={f.key}>
                      <label className="label-base flex items-center gap-1">
                        <Icon size={12} /> {t(`adminX.config.fields.${f.key}`)}
                      </label>
                      <input
                        type="number"
                        step={f.step}
                        value={value(f.key) ?? ""}
                        onChange={(e) => setField(f.key, e.target.value === "" ? null : Number(e.target.value))}
                        className="input-base"
                        data-testid={`config-${f.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {cfg?.updated_at && (
          <p className="mt-6 text-xs text-smoke font-mono">
            {t("adminX.config.lastUpdated")} {formatDateTime(cfg.updated_at)}
          </p>
        )}
      </div>
    </DashboardLayout>
  );
}
